import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';

interface LoginResponse {
  id: string;
  email: string;
  display_name: string;
  role: string;
}

export default function LoginPage({ redirectTo }: { redirectTo: string }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const qc = useQueryClient();

  const login = useMutation({
    mutationFn: () => api.post<LoginResponse>('/auth/login', { email, password }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['me'] });
      navigate({ to: redirectTo });
    },
  });

  return (
    <div className="max-w-md mx-auto mt-16 p-6">
      <div className="mb-6 flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold tracking-tight text-accent">Granted</span>
        <span className="text-sm text-neutral-500">by Commondoor</span>
      </div>
      <h1 className="text-xl font-semibold mb-4">Sign in</h1>
      <form
        className="space-y-3"
        onSubmit={(e) => { e.preventDefault(); login.mutate(); }}
      >
        <label className="block">
          <span className="block text-sm text-neutral-700 mb-1">Email</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border border-neutral-300 rounded-md px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="block text-sm text-neutral-700 mb-1">Password</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-neutral-300 rounded-md px-3 py-2"
          />
        </label>
        <button
          type="submit"
          disabled={login.isPending}
          className="w-full bg-accent text-white rounded-md py-2 disabled:opacity-60"
        >
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </button>
        {login.error instanceof ApiError && (
          <p className="text-sm text-rose-600" role="alert">{login.error.message}</p>
        )}
      </form>
    </div>
  );
}
